import { useMemo } from 'react';
import { useApi } from '../infrastructure/useApi';
import { DEFAULT_API_OPTIONS } from '../infrastructure/defaults';
import { API_URLS } from '../apiUrls';
import { API_METHODS } from '../constants/apiMethods';

/**
 * Hook for accessing User API methods.
 *
 * Usage:
 * const { getProfile, getAccounts, getAccountDetails, impersonate, cancel } =
 * 	useUserApi();
 * await getProfile(userId);
 *
 * Cancellation scope:
 * - cancel() applies to requests started by this hook instance.
 * - For independent cancellation scopes, create separate hook instances.
 */
export const useUserApi = (baseOptions = DEFAULT_API_OPTIONS) => {
	const { call, cancel } = useApi(baseOptions);

	return useMemo(
		() => ({
			getProfile: (userId) =>
				call({
					url: API_URLS.user.getProfile(userId),
					method: API_METHODS.GET,
				}),

			updateProfile: (userId, profileData) =>
				call({
					url: API_URLS.user.updateProfile(userId),
					method: API_METHODS.PUT,
					payload: profileData,
				}),

			getAccounts: (userId) =>
				call({
					url: API_URLS.user.getAccounts(userId),
					method: API_METHODS.GET,
				}),

			getAccountDetails: (userId, accountId, abortController = null) =>
				call({
					url: API_URLS.user.getAccountDetails(userId, accountId),
					method: API_METHODS.GET,
					abortController,
				}),

			impersonate: (userId) =>
				call({
					url: API_URLS.user.impersonate(userId),
					method: API_METHODS.POST,
				}),

			getNavFilters: () =>
				call({
					url: API_URLS.user.getNavFilters(),
					method: API_METHODS.GET,
				}),

			getProductFilters: () =>
				call({
					url: API_URLS.user.getProductFilters(),
					method: API_METHODS.GET,
				}),

			cancel,
		}),
		[call, cancel]
	);
};
